import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google'; // <--- NEW IMPORT
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
    const navigate = useNavigate();
    const { login, setAuthData } = useAuth(); // Assuming setAuthData exposes the internal state updater, otherwise we might need to manually set local storage and reload, or update AuthContext to support google login. 
    // Actually, usually 'login' takes email/pass. We need a way to set the session from the Google token.
    // Ideally, add a 'loginWithGoogle' to your AuthContext, or just manual handle here:

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            const { data } = await axios.post(`${API}/auth/google`, {
                token: credentialResponse.credential
            });

            // 1. Save Token
            localStorage.setItem('subhack_token', data.access_token);
            
            // 2. Check for Stashed Guest Work
            const returnUrl = localStorage.getItem('subhack_return_url');

            // 3. Smart Redirect
            if (returnUrl) {
                // If we have saved work, go there
                window.location.href = returnUrl;
            } else {
                // Default behavior for normal logins
                window.location.href = '/dashboard';
            }

        } catch (error) {
            console.error(error);
            toast.error('Google Sign-In failed');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const returnUrl = localStorage.getItem('subhack_return_url');

        try {
            await login(email, password);
            toast.success('Welcome back!');
            if (returnUrl) {
                window.location.href = returnUrl;
            } else {
                navigate('/dashboard');
            }
        } catch (error) {
            const message = error.response?.data?.detail || 'Login failed. Please try again.';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-background">
            <div className="p-4">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="font-heading text-sm uppercase tracking-wider">Back to Home</span>
                </Link>
            </div>

            <div className="flex-1 flex items-center justify-center px-4">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <Link to="/">
                            <span className="font-brand text-4xl text-primary">SUBHACK</span>
                        </Link>
                        <h1 className="font-heading text-2xl uppercase mt-6 mb-2">Welcome Back</h1>
                        <p className="text-muted-foreground text-sm">
                            Sign in to continue creating
                        </p>
                    </div>

                    <div className="card-base p-8">
                        {/* GOOGLE LOGIN BUTTON - BIGGER & BOLDER */}
                        <div className="mb-8 flex justify-center transform hover:scale-105 transition-transform duration-200">
                            <div className="shadow-md rounded-full overflow-hidden border border-border/50">
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={() => toast.error('Google Sign-In Failed')}
                                    theme="filled_black"
                                    shape="pill"
                                    width="360" // Increased from 320 to match standard input width better
                                    logo_alignment="left"
                                />
                            </div>
                        </div>

                        

                        <div className="relative mb-6">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-border" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="font-heading text-sm uppercase tracking-wider">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="input-base h-12"
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                    <Label htmlFor="password" className="font-heading text-sm uppercase tracking-wider">Password</Label>
                                    <Link to="/forgot-password" className="text-xs text-primary hover:underline">Forgot password?</Link>
                                </div>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="input-base h-12"
                                />
                            </div>

                            <Button type="submit" className="btn-primary w-full h-12" disabled={loading}>
                                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : 'Sign In'}
                            </Button>
                        </form>
                    </div>

                    <p className="text-center mt-6 text-sm text-muted-foreground">
                        Don't have an account?{' '}
                        <Link to="/register" className="text-primary hover:underline">Create one</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}